import {FC, useEffect, useMemo, useState} from "react";
import {ConfigProvider, Table, Tag, Typography, theme} from "antd";
import {useSelector} from "react-redux";
import AxiosServices from "../Services/axiosServices";
import {contrastColor} from "../Services/color";
import {useAuth} from "../config/AuthContext";
import {useGlobal} from "../config/GlobalContext";
import {IGrpCmpEdt} from "../Interfaces/IGrpCmpEdt";
import {RootState} from "../store";
import MarqueeText from "./MarqueeText";

const {Text} = Typography;

type StandingRow = {
    rang: number;
    team_idt: number;
    team: string;
    mp: number;
    w: number;
    d: number;
    l: number;
    gf: number;
    ga: number;
    gd: number;
    pts: number;
};

const noWrap = {whiteSpace: 'nowrap' as const, fontSize: 11};

const miniColumns = [
    {title: '#', dataIndex: 'rang', width: 26, align: 'center' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {
        title: 'Équipe',
        dataIndex: 'team',
        width: 80,
        onCell: () => ({style: {maxWidth: 80, overflow: 'hidden', fontSize: 11}}),
        render: (v: string) => <MarqueeText text={v ?? ''} fontSize={11}/>,
    },
    {title: 'J', dataIndex: 'mp', width: 28, align: 'right' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {
        title: 'Diff',
        dataIndex: 'gd',
        width: 44,
        align: 'right' as const,
        onHeaderCell: () => ({style: noWrap}),
        onCell: () => ({style: noWrap}),
        render: (v: number) => (v > 0 ? `+${v}` : v),
    },
    {
        title: 'Pts',
        dataIndex: 'pts',
        width: 38,
        align: 'right' as const,
        onHeaderCell: () => ({style: noWrap}),
        onCell: () => ({style: noWrap}),
        render: (v: number) => <Text strong style={{fontSize: 11}}>{v}</Text>,
    },
];

type GroupStandingsPairProps = {
    refreshKey?: number;
};

const GroupStandingsPair: FC<GroupStandingsPairProps> = ({refreshKey = 0}) => {
    const {isLoggedIn} = useAuth();
    const {currentCmpEdt, groups, selectedGroupIds} = useGlobal();
    const allBets = useSelector((state: RootState) => state.bets.userBets);
    const [officialByGroup, setOfficialByGroup] = useState<Record<string, StandingRow[]>>({});
    const [betsByGroup, setBetsByGroup] = useState<Record<string, StandingRow[]>>({});

    const visibleGroups = useMemo<IGrpCmpEdt[]>(() => {
        if (selectedGroupIds.length === 0) return groups;
        return groups.filter(g => selectedGroupIds.includes(g.Idt));
    }, [groups, selectedGroupIds]);

    useEffect(() => {
        if (!currentCmpEdt || visibleGroups.length === 0) {
            setOfficialByGroup({});
            return;
        }
        let cancelled = false;
        Promise.all(
            visibleGroups
                .filter(g => !!g.Grp_Cod)
                .map(g =>
                    AxiosServices.groupStandings(currentCmpEdt, g.Grp_Cod as string)
                        .then(rows => [g.Grp_Cod as string, rows] as const)
                        .catch((err: any) => {
                            console.error('groupStandings failed for', g.Grp_Cod, err);
                            return [g.Grp_Cod as string, [] as StandingRow[]] as const;
                        }),
                ),
        ).then(results => {
            if (cancelled) return;
            const map: Record<string, StandingRow[]> = {};
            results.forEach(([code, rows]) => { map[code] = rows; });
            setOfficialByGroup(map);
        });
        return () => { cancelled = true; };
    }, [currentCmpEdt, visibleGroups, refreshKey]);

    useEffect(() => {
        if (!isLoggedIn || !currentCmpEdt || visibleGroups.length === 0) {
            setBetsByGroup({});
            return;
        }
        let cancelled = false;
        Promise.all(
            visibleGroups
                .filter(g => !!g.Grp_Cod)
                .map(g =>
                    AxiosServices.groupStandingsByUserBets(currentCmpEdt, g.Grp_Cod as string)
                        .then(rows => [g.Grp_Cod as string, rows] as const)
                        .catch((err: any) => {
                            console.error('groupStandingsByUserBets failed for', g.Grp_Cod, err);
                            return [g.Grp_Cod as string, [] as StandingRow[]] as const;
                        }),
                ),
        ).then(results => {
            if (cancelled) return;
            const map: Record<string, StandingRow[]> = {};
            results.forEach(([code, rows]) => { map[code] = rows; });
            setBetsByGroup(map);
        });
        return () => { cancelled = true; };
    }, [currentCmpEdt, isLoggedIn, visibleGroups, allBets]);

    if (visibleGroups.length === 0) return null;

    return (
        <ConfigProvider theme={{algorithm: theme.darkAlgorithm}}>
            <div style={{padding: 8, display: 'flex', flexDirection: 'column', gap: 14}}>
                {visibleGroups.map(g => {
                    const official = officialByGroup[g.Grp_Cod as string] ?? [];
                    const bets = betsByGroup[g.Grp_Cod as string] ?? [];
                    return (
                        <div key={g.Idt}>
                            <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4}}>
                                <Tag
                                    style={{
                                        margin: 0,
                                        fontWeight: 600,
                                        minWidth: 22,
                                        textAlign: 'center',
                                        background: g.clr || undefined,
                                        borderColor: g.clr || undefined,
                                        color: contrastColor(g.clr),
                                    }}
                                >
                                    {(g.Grp_Cod ?? '').slice(0, 1).toUpperCase()}
                                </Tag>
                                <Text strong style={{fontSize: 13, color: '#fff'}}>{g.lib}</Text>
                            </div>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8}}>
                                <div style={{minWidth: 0}}>
                                    <Text style={{fontSize: 10, letterSpacing: 0.5, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase'}}>
                                        Officiel
                                    </Text>
                                    {official.length === 0 ? (
                                        <Text italic style={{display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)'}}>
                                            Aucun match joué.
                                        </Text>
                                    ) : (
                                        <div style={{borderRadius: '0 0 8px 8px', overflow: 'hidden'}}>
                                            <Table
                                                size="small"
                                                pagination={false}
                                                showHeader
                                                dataSource={official}
                                                rowKey="team_idt"
                                                columns={miniColumns as any}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div style={{minWidth: 0}}>
                                    <Text style={{fontSize: 10, letterSpacing: 0.5, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase'}}>
                                        Selon mes paris
                                    </Text>
                                    {!isLoggedIn ? (
                                        <Text italic style={{display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)'}}>
                                            Connecte-toi pour voir.
                                        </Text>
                                    ) : bets.length === 0 ? (
                                        <Text italic style={{display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)'}}>
                                            Aucun pari saisi.
                                        </Text>
                                    ) : (
                                        <div style={{borderRadius: '0 0 8px 8px', overflow: 'hidden'}}>
                                            <Table
                                                size="small"
                                                pagination={false}
                                                showHeader
                                                dataSource={bets}
                                                rowKey="team_idt"
                                                columns={miniColumns as any}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </ConfigProvider>
    );
};

export default GroupStandingsPair;
